import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getApiBase } from '../api';
import { useAuth } from '../components/AuthContext';

const statusConfig = {
  IN_TRANSIT:  { emoji: '🚛', color: '#2563eb', label: 'In Transit', markerColor: '#3b82f6' },
  PICKED_UP:   { emoji: '📦', color: '#d97706', label: 'Picked Up', markerColor: '#f59e0b' },
  IN_PROGRESS: { emoji: '🔄', color: '#7c3aed', label: 'In Progress', markerColor: '#8b5cf6' },
  TENDERED:    { emoji: '📋', color: '#0891b2', label: 'Tendered', markerColor: '#06b6d4' },
  POSTED:      { emoji: '📢', color: '#dc2626', label: 'Posted', markerColor: '#ef4444' },
};

function money(cents) {
  if (!cents && cents !== 0) return '—';
  return `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

function etaDisplay(eta) {
  if (!eta) return '—';
  const d = new Date(eta);
  const now = new Date();
  const diffMs = d - now;
  const diffHrs = Math.round(diffMs / 3600000);
  if (diffHrs <= 0) return 'Arriving...';
  if (diffHrs < 24) return `${diffHrs}h remaining`;
  return `${Math.round(diffHrs / 24)}d ${diffHrs % 24}h`;
}

export default function ShipperTracking() {
  const apiBase = useMemo(() => getApiBase(), []);
  const { shipperEmail } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!shipperEmail) return;
    setLoading(true);
    fetch(`${apiBase}/shipper/tracking`, {
      headers: { 'x-shipper-email': shipperEmail }
    })
      .then(r => { if (!r.ok) throw new Error('Failed to load tracking data'); return r.json(); })
      .then(d => { setShipments(d.shipments || []); setError(''); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [apiBase, shipperEmail]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!shipperEmail) return;
    const interval = setInterval(() => {
      fetch(`${apiBase}/shipper/tracking`, {
        headers: { 'x-shipper-email': shipperEmail }
      })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setShipments(d.shipments || []); });
    }, 30000);
    return () => clearInterval(interval);
  }, [apiBase, shipperEmail]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    script.onload = () => {
      const L = window.L;
      const map = L.map(mapRef.current).setView([31.5, -99.5], 6);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(map);
      mapInstance.current = map;
    };
    document.body.appendChild(script);
    return () => { if (mapInstance.current) mapInstance.current.remove(); mapInstance.current = null; };
  }, []);

  // Update markers when shipments change
  useEffect(() => {
    if (!mapInstance.current || !window.L) return;
    const L = window.L;
    const map = mapInstance.current;

    // Clear old markers
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    if (shipments.length === 0) return;

    const bounds = [];
    shipments.forEach(s => {
      const sc = statusConfig[s.status] || { markerColor: '#6b7280', emoji: '❓' };

      // Origin marker (green dot)
      const originIcon = L.divIcon({
        className: '',
        html: `<div style="width:12px;height:12px;background:#10b981;border:2px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>`,
        iconSize: [12, 12], iconAnchor: [6, 6]
      });
      const om = L.marker([s.originLat, s.originLng], { icon: originIcon })
        .bindTooltip(`📍 Origin: ${s.originCity}, ${s.originState}`, { direction: 'top' });
      om.addTo(map);
      markersRef.current.push(om);

      // Dest marker (red dot)
      const destIcon = L.divIcon({
        className: '',
        html: `<div style="width:12px;height:12px;background:#ef4444;border:2px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>`,
        iconSize: [12, 12], iconAnchor: [6, 6]
      });
      const dm = L.marker([s.destLat, s.destLng], { icon: destIcon })
        .bindTooltip(`🏁 Dest: ${s.destCity}, ${s.destState}`, { direction: 'top' });
      dm.addTo(map);
      markersRef.current.push(dm);

      // Current position (truck icon)
      const truckIcon = L.divIcon({
        className: '',
        html: `<div style="font-size:24px;text-shadow:0 2px 4px rgba(0,0,0,0.3);cursor:pointer">${sc.emoji}</div>`,
        iconSize: [28, 28], iconAnchor: [14, 14]
      });
      const tm = L.marker([s.currentLat, s.currentLng], { icon: truckIcon })
        .bindPopup(`<b>${s.loadNumber}</b><br>${s.originCity} → ${s.destCity}<br>Status: ${sc.emoji} ${s.status}`);
      tm.on('click', () => setSelected(s));
      tm.addTo(map);
      markersRef.current.push(tm);

      // Route line (dashed)
      const line = L.polyline([
        [s.originLat, s.originLng],
        [s.currentLat, s.currentLng],
        [s.destLat, s.destLng]
      ], { color: sc.markerColor, weight: 2, dashArray: '8, 6', opacity: 0.6 });
      line.addTo(map);
      markersRef.current.push(line);

      bounds.push([s.originLat, s.originLng], [s.destLat, s.destLng], [s.currentLat, s.currentLng]);
    });

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [shipments]);

  if (!shipperEmail) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
      Please set a Shipper Email in the dev panel.
    </div>
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a, #1e40af)',
        borderRadius: 14, padding: '20px 24px', marginBottom: 20, color: 'white',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>📍 Live Shipment Tracking</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.7 }}>
            {shipments.length} active shipment{shipments.length !== 1 ? 's' : ''} • Auto-refreshes every 30s
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(statusConfig).map(([k, v]) => {
            const count = shipments.filter(s => s.status === k).length;
            if (count === 0) return null;
            return (
              <span key={k} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.15)', color: 'white' }}>
                {v.emoji} {count}
              </span>
            );
          })}
        </div>
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', borderRadius: 8, color: '#991b1b', marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 16, minHeight: 500 }}>
        {/* Map */}
        <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid #e5e7eb', position: 'relative' }}>
          {loading && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              Loading map...
            </div>
          )}
          <div ref={mapRef} style={{ width: '100%', height: 550 }} />
        </div>

        {/* Sidebar - Shipment List */}
        <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', fontWeight: 800, fontSize: 15 }}>
            Active Shipments
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {shipments.length === 0 && !loading ? (
              <div style={{ padding: 30, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                No active shipments with tracking data
              </div>
            ) : (
              shipments.map(s => {
                const sc = statusConfig[s.status] || { emoji: '❓', color: '#6b7280', label: s.status };
                const isSelected = selected?.id === s.id;
                return (
                  <div key={s.id} onClick={() => {
                    setSelected(s);
                    if (mapInstance.current) mapInstance.current.setView([s.currentLat, s.currentLng], 8);
                  }}
                    style={{
                      padding: '14px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                      background: isSelected ? '#eff6ff' : 'white',
                      borderLeft: isSelected ? `3px solid ${sc.color}` : '3px solid transparent',
                      transition: 'all 0.15s'
                    }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontWeight: 800, fontSize: 14 }}>{s.loadNumber}</span>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: `${sc.color}15`, color: sc.color }}>
                        {sc.emoji} {sc.label}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>
                      {s.originCity}, {s.originState} → {s.destCity}, {s.destState}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280' }}>
                      <span>⏱ {etaDisplay(s.eta)}</span>
                      <span>{s.distanceMiles ? `${s.distanceMiles} mi` : ''}</span>
                    </div>
                    {s.carrierName && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Carrier: {s.carrierName}</div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Selected shipment detail */}
      {selected && (
        <div style={{ marginTop: 16, background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
              {selected.loadNumber} — {selected.originCity} to {selected.destCity}
            </h3>
            <button onClick={() => setSelected(null)}
              style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9ca3af' }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Status</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                {(statusConfig[selected.status] || {}).emoji} {selected.status}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>ETA</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                {selected.eta ? new Date(selected.eta).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Distance</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{selected.distanceMiles || '—'} mi</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Carrier</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{selected.carrierName || 'Pending'}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
